import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';

interface FileFormatImportProps {
  readonly onImportJson: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly importError: string | null;
}

interface FileFormatExportProps {
  readonly onExportJson: () => void;
  readonly onExportSvg: () => void;
  readonly onPrint?: (() => void) | undefined;
}

// ADR 0007 P10a-4 — the drawing document as JSON, or a standalone SVG image. Rehomes the file
// group from `/draw`'s DrawingToolNav (Import JSON / Export JSON / Export SVG) into the File
// drawer; `WorkspaceShell` builds the handlers exactly as `ChemicalStructureEditor` does.
// #837 (owner, #836) -- split into the drawer's Import group (open a JSON file) and its Export
// group (the export dialog and JSON).
export function FileFormatImport({ onImportJson, importError }: FileFormatImportProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <section className="file-format-panel" aria-labelledby="file-format-import-heading">
      <div>
        <h2 id="file-format-import-heading">{t('workspace.fileFormats.importHeading')}</h2>
        <p>{t('workspace.fileFormats.importSubheading')}</p>
      </div>
      <div className="file-format-actions">
        <button
          type="button"
          className="icon-button"
          onClick={() => inputRef.current?.click()}
          aria-label={t('workspace.fileFormats.importJson')}
          title={t('workspace.fileFormats.importJson')}
        >
          <Icon name="uploadFile" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          className="visually-hidden"
          onChange={onImportJson}
        />
      </div>
      {importError ? (
        <p className="import-error" role="alert">
          {importError}
        </p>
      ) : null}
    </section>
  );
}

export function FileFormatExport({ onExportJson, onExportSvg, onPrint }: FileFormatExportProps) {
  const { t } = useTranslation();
  return (
    <section className="file-format-panel" aria-label={t('workspace.fileDrawer.groups.export')}>
      <p>{t('workspace.fileFormats.subheading')}</p>
      <div className="file-format-actions">
        {onPrint ? (
          <button
            type="button"
            className="icon-button"
            onClick={onPrint}
            aria-label={t('printing.drawing')}
            title={t('printing.drawing')}
          >
            <Icon name="print" />
          </button>
        ) : null}
        <button
          type="button"
          className="icon-button"
          onClick={onExportSvg}
          aria-label={t('workspace.fileFormats.exportImage')}
          title={t('workspace.fileFormats.exportImage')}
        >
          <Icon name="image" />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={onExportJson}
          aria-label={t('workspace.fileFormats.exportJson')}
          title={t('workspace.fileFormats.exportJson')}
        >
          <Icon name="download" />
        </button>
      </div>
    </section>
  );
}
